import { t } from '@glory/i18n';
import {
  Button,
  Checkbox,
  ContentSwap,
  notify,
  PageHeader,
  SettingsSection,
  SkeletonRows,
  Tooltip,
} from '@glory/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Send } from 'lucide-react';

import { MessengerNotificationSettings } from '../../../features/messenger/ui/push-text-setting.tsx';
import { PushSection } from '../../../features/notifications/push-section.tsx';
import { orpc } from '../../../lib/orpc.ts';

export const Route = createFileRoute('/_app/settings/notifications')({
  component: NotificationSettingsPage,
});

const CHANNELS = [
  { key: 'inApp', label: t('notifications.channelInApp') },
  { key: 'push', label: t('notifications.channelPush') },
  { key: 'email', label: t('notifications.channelEmail') },
] as const;

function NotificationSettingsPage() {
  const queryClient = useQueryClient();
  const matrix = useQuery(orpc.notifications.preferences.queryOptions());
  const key = orpc.notifications.preferences.queryKey();
  const toggle = useMutation(
    orpc.notifications.setPreference.mutationOptions({
      // Флажок переключается сразу; при ошибке возвращаем как было.
      onMutate: async ({ type, channel, enabled }) => {
        await queryClient.cancelQueries({ queryKey: key });
        const previous = queryClient.getQueryData(key);
        queryClient.setQueryData(key, (rows) =>
          rows?.map((row) =>
            row.type === type ? { ...row, channels: { ...row.channels, [channel]: enabled } } : row,
          ),
        );
        return { previous };
      },
      onError: (_error, _input, context) => {
        if (context?.previous) queryClient.setQueryData(key, context.previous);
      },
      onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
    }),
  );
  const sendTest = useMutation(
    orpc.notifications.sendTest.mutationOptions({
      onSuccess: () => {
        notify(t('notifications.testSent'));
      },
    }),
  );

  return (
    <div className="flex flex-col gap-4 px-4 py-5 md:px-6">
      <PageHeader
        title={t('notifications.settingsTitle')}
        actions={
          <Button
            loading={sendTest.isPending}
            onClick={() => {
              sendTest.mutate({});
            }}
          >
            <Send /> {t('notifications.sendTest')}
          </Button>
        }
      />
      <p className="-mt-2 text-muted">{t('notifications.settingsLead')}</p>
      <p className="-mt-2 text-small text-muted">{t('notifications.emailInsteadOfPush')}</p>
      <PushSection />
      <MessengerNotificationSettings />
      <SettingsSection title={t('notifications.matrixTitle')}>
        <ContentSwap swapKey={matrix.isPending ? 'loading' : matrix.isError ? 'error' : 'ready'}>
          {matrix.isPending ? (
            <SkeletonRows rows={3} />
          ) : matrix.isError ? null : (
            <div role="table" aria-label={t('notifications.title')} className="flex flex-col">
              <div
                role="row"
                className="grid grid-cols-[1fr_repeat(3,4.5rem)] sm:grid-cols-[1fr_repeat(3,5.5rem)] gap-2 pb-2 text-caption font-bold text-muted"
              >
                <span role="columnheader" />
                {CHANNELS.map((c) => (
                  <span role="columnheader" key={c.key} className="text-center">
                    {c.label}
                  </span>
                ))}
              </div>
              {matrix.data.map((row) => (
                <div
                  role="row"
                  key={row.type}
                  className="grid grid-cols-[1fr_repeat(3,4.5rem)] sm:grid-cols-[1fr_repeat(3,5.5rem)] items-center gap-2 border-t border-border-soft py-2.5"
                >
                  <div role="cell" className="flex min-w-0 flex-col">
                    <span className="font-semibold">{row.label}</span>
                    <span className="text-caption text-muted">{row.description}</span>
                  </div>
                  {CHANNELS.map((c) => {
                    const locked = row.locked.includes(c.key);
                    const box = (
                      <Checkbox
                        aria-label={`${row.label}: ${c.label}`}
                        checked={row.channels[c.key]}
                        disabled={locked}
                        onCheckedChange={(enabled) => {
                          toggle.mutate({ type: row.type, channel: c.key, enabled });
                        }}
                      />
                    );
                    return (
                      <span role="cell" key={c.key} className="flex justify-center">
                        {locked ? (
                          <Tooltip content={t('notifications.locked')}>
                            <span>{box}</span>
                          </Tooltip>
                        ) : (
                          box
                        )}
                      </span>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </ContentSwap>
      </SettingsSection>
    </div>
  );
}
